import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Flatpickr from "react-flatpickr";
import makeAnimated from "react-select/animated";
import SelectTree from "Components/Common/SelectTree/SelectTree";
import conditionsJson from "./conditionsJson.json";
import { isJson } from "utils";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "store";
import { optionsListGetRequest } from "store/auth/action";
import { currencyGetRequest } from "store/currency/action";
import { getStatusOptions } from "helpers/workflowStatusHelper";
import hasPermission from "helpers/hasPermission";
import { createSelector, ThunkDispatch } from "@reduxjs/toolkit";
import { Action } from "redux";

const animatedComponents = makeAnimated();

const AddFilter = ({ dataFields, onFilterSelected, selectedGroup, filter, setSelectedGroupIndex }: any) => {
	const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
	const optionsObj = useSelector((state: ApplicationState) => state.auth.optionData);
	const optionsLoading = useSelector((state: ApplicationState) => state.auth.optionsLoading);
	const conversionRates = useSelector((state: ApplicationState) => state.currency.conversion);
	const updateCurrencyState = useSelector((state: ApplicationState) => state.currency.updatedCurrency);
	const userProfile: any = useSelector((state: ApplicationState) => state.auth.userProfile);

	const selectDashboardData = createSelector(
		(state: any) => state.Layout,
		(state) => ({ layoutModeType: state.layoutModeType })
	);
	const { layoutModeType } = useSelector(selectDashboardData);

	const [properties, setProperties] = useState<any[]>([]);
	const [selectedProperty, setSelectedProperty] = useState<any>(null);
	const [conditions, setConditions] = useState<any[]>([]);
	const [selectedCondition, setSelectedCondition] = useState<any>(null);
	const [options, setOptions] = useState<any[]>([]);
	const [values, setValues] = useState<any[]>([]);
	const [value, setValue] = useState<any>(null);
	const [highValue, setHighValue] = useState<any>(null);
	const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
	const [selectedCurrency, setSelectedCurrency] = useState<any>(null);

	const customStyles = {
		input: (provided: any) => ({
			...provided,
			color: layoutModeType === "light" ? "black" : "white",
		}),
		control: (provided: any) => ({
			...provided,
			backgroundColor: "transparent",
		}),
		menu: (provided: any) => ({
			...provided,
			backgroundColor: layoutModeType === "light" ? "white" : "#495057",
			color: layoutModeType === "light" ? "black" : "white",
		}),
		option: (provided: any) => ({
			...provided,
			backgroundColor: layoutModeType === "light" ? "white" : "#495057",
			color: layoutModeType === "light" ? "black" : "white",
			"&:hover": {
				backgroundColor: layoutModeType === "light" ? "#f8f9fa" : "#343a40",
			},
		}),
	};

	useEffect(() => { dispatch(currencyGetRequest()); }, []);
	useEffect(() => {
		if (updateCurrencyState) {
			setSelectedCurrency(updateCurrencyState.selectedCurrency);
		}
	}, [updateCurrencyState]);

	useEffect(() => {
		const mapped = dataFields
			?.map((item: any) => {
				if (item.permissionsJson && !hasPermission(item.permissionsJson, userProfile?.role?.id)) return null;
				return {
					label: item.Header,
					value: item.accessor,
					type: item?.valueJson?.type || "datetime",
					key: item?.valueJson?.key,
					valueJson: item.valueJson || {},
				};
			})
			.filter(Boolean);
		setProperties(mapped);
	}, [dataFields, userProfile]);

	useEffect(() => {
		if (filter && filter?.property) {
			const property = filter?.property;
			setSelectedProperty(property);
			const conditionOpts = conditionsJson[property.type as keyof typeof conditionsJson] || [];
			setConditions(conditionOpts);
			setSelectedCondition({ value: filter?.condition, label: filter?.conditionLabel });
			setValues(filter?.valuesLabel);
			setValue(filter?.value);
			setHighValue(filter?.highValue);
		} else {
			clearFilter();
		}
	}, [filter]);

	const clearFilter = useCallback(() => {
		setSelectedProperty(null);
		setConditions([]);
		setSelectedCondition(null);
		setValues([]);
		setValue(null);
		setHighValue(null);
	}, []);

	const onFocus = () => {
		setOptions([]);
		if (selectedProperty?.valueJson?.model) {
			const filter = {
				filters: [{ quick: [], advance: [], search: null }],
				sorts: [{ field: "name", order: "asc" }],
			};
			const isTree = selectedProperty?.valueJson?.model === "sources" || selectedProperty?.valueJson?.responseType === "treeselect";
			dispatch(optionsListGetRequest(selectedProperty?.valueJson?.model, filter, 0, 20, isTree ? { responseType: "treeselect" } : null, "field"));
		}
	};

	const handleTypeText = (value: string) => {
		if (typingTimeout) clearTimeout(typingTimeout);
		const newTimeout = setTimeout(() => {
			const filter = {
				filters: [{ search: { term: value, field: "all" } }],
				sorts: [{ field: "name", order: "asc" }],
			};
			const isTree = selectedProperty?.valueJson?.model === "sources" || selectedProperty?.valueJson?.responseType === "treeselect";
			dispatch(optionsListGetRequest(selectedProperty?.valueJson?.model, filter, 0, 20, isTree ? { responseType: "treeselect" } : null, "field"));
		}, 500);
		setTypingTimeout(newTimeout);
	};

	useEffect(() => {
		const model = selectedProperty?.valueJson?.model;
		const data = optionsObj?.[model];
		if (!data) return;

		if (selectedProperty?.type === "treeselect") {
			const transformTree = (node: any): any => {
				const values = isJson(node?.valuesJson) ? JSON.parse(node?.valuesJson) : node?.valuesJson;
				return {
					label: values?.name,
					value: node?.id,
					children: node?.children?.map(transformTree) || [],
				};
			};
			setOptions(data.map(transformTree));
		} else {
			const newOptions = data.map((option: any) => {
				const json = isJson(option.valuesJson) ? JSON.parse(option.valuesJson) : option.valuesJson;
				return {
					label: json?.[selectedProperty?.valueJson?.optionLabel],
					value: option.id,
					valuesJson: json,
				};
			});
			setOptions(newOptions);
		}
	}, [optionsObj, selectedProperty]);

	const onSelectProperty = (property: any) => {
		setSelectedProperty(property);
		const conditionOpts = conditionsJson[property.type as keyof typeof conditionsJson];
		setConditions(conditionOpts);
		setSelectedCondition(conditionOpts?.[0]);
		setValues([]);
		setValue(null);
		setHighValue(null);
	};

	const renderValueInput = () => {
		switch (selectedProperty?.type) {
			case "datasetselect":
				return (
					<Select
						onFocus={onFocus}
						onInputChange={handleTypeText}
						isMulti
						isClearable
						isSearchable
						styles={customStyles}
						components={animatedComponents}
						options={optionsLoading ? [{ label: "Loading...", value: null }] : options}
						isLoading={optionsLoading}
						value={values}
						onChange={setValues}
					/>
				);
			case "select":
			case "checkbox":
			case "selectboxes":
				const builtOptions = selectedProperty?.valueJson?.process_flows?.length
					? getStatusOptions(selectedProperty?.valueJson?.process_flows).options
					: selectedProperty?.valueJson?.values || [];
				return (
					<Select
						isMulti
						isClearable
						styles={customStyles}
						options={builtOptions.filter((opt: any) => opt.in_forms)}
						value={values}
						onChange={setValues}
					/>
				);
			case "treeselect":
				return (
					<SelectTree
						values={values}
						setValues={setValues}
						optionsLoading={optionsLoading}
						showValues
						options={options}
						onFocus={onFocus}
					/>
				);
			case "textfield":
			case "textarea":
			case "email":
			case "phone":
				return (
					<CreatableSelect
						isMulti
						isClearable
						components={animatedComponents}
						value={values}
						onChange={(newValue: any) => setValues(newValue)}
					/>
				);
			case "datetime":
				return selectedCondition?.value === "BETWEEN" ? (
					<div className="d-flex flex-column gap-2">
						<Flatpickr className="form-control" value={value} onChange={([date]) => setValue(date)} />
						<Flatpickr className="form-control" value={highValue} onChange={([date]) => setHighValue(date)} />
					</div>
				) : (
					<Flatpickr className="form-control" value={value} onChange={([date]) => setValue(date)} />
				);
			case "number":
				return selectedCondition?.value === "BETWEEN" ? (
					<div className="d-flex flex-column gap-2">
						<Input type="number" value={value ?? ""} onChange={(e) => setValue(e.target.value)} />
						<Input type="number" value={highValue ?? ""} onChange={(e) => setHighValue(e.target.value)} />
					</div>
				) : (
					<Input type="number" value={value ?? ""} onChange={(e) => { setValue(e.target.value); setHighValue(null); }} />
				);
			default:
				return null;
		}
	};

	const handleSave = () => {
		onFilterSelected({
			key: selectedProperty?.value,
			keyLabel: selectedProperty?.label,
			condition: selectedCondition?.value,
			conditionLabel: selectedCondition?.label,
			values: values?.map((v: any) => v.value),
			valuesLabel: values,
			property: selectedProperty,
			value,
			highValue,
			currency: selectedCurrency
		}, selectedGroup);
		clearFilter();
	};

	return (
		<div className="w-50 bg-light px-4 py-4 d-flex flex-column gap-3 border-start">
			<div className="d-flex justify-content-between align-items-center mb-2">
				<h5 className="fw-semibold mb-0">Define Condition</h5>
				<i className="ri-close-line fs-4 text-muted" onClick={() => setSelectedGroupIndex(null)} />
			</div>

			<div className="d-flex flex-column gap-2">
				<Label className="fw-semibold">Choose Field</Label>
				<small className="text-muted mb-1">Select the lead field to apply the condition on</small>
				<Select
					isClearable
					options={properties}
					value={selectedProperty}
					onChange={onSelectProperty}
				/>
			</div>

			{selectedProperty && (
				<>
					<div className="d-flex flex-column gap-2">
						<Label className="fw-semibold">Condition</Label>
						<small className="text-muted mb-1">Choose how the selected field should be matched</small>
						<Select
							options={conditions}
							value={selectedCondition}
							onChange={(c: any) => { setSelectedCondition(c); setHighValue(null); }}
						/>
					</div>

					<div className="d-flex flex-column gap-2">
						<Label className="fw-semibold">Value</Label>
						<small className="text-muted mb-1">Set the value(s) for this condition</small>
						{renderValueInput()}
					</div>

					<div className="d-flex justify-content-end mt-3">
						<Button
							color="primary"
							disabled={
								!(selectedCondition?.value === "BETWEEN"
									? value && highValue && Number(highValue) >= Number(value)
									: values?.length || value)
							}
							onClick={handleSave}
						>
							<i className="ri-check-line me-2" />
							Save Condition
						</Button>
					</div>
				</>
			)}
		</div>
	);
};

export default AddFilter;
